import axios, { AxiosRequestConfig } from 'axios';

import FormErrors from './FormErrors';

interface FormDataOptions {
  validate?: boolean;
  invalidValues?: any[];
}

class FormDataProxy<T = Record<string, any>> {
  [key: string]: any;

  constructor(
    data: Partial<T> = {},
    defaults: Partial<T> = {},
    options: FormDataOptions = { validate: false, invalidValues: [] },
  ) {
    const normalizedDefaults: Record<string, any> = {};

    if (Object.keys(defaults).some((key) => key.includes('.'))) {
      Object.entries(defaults).forEach(([key, value]) => {
        if (!key.includes('.')) {
          normalizedDefaults[key] = value;
          return;
        }
        const path = key.split('.');
        if (!normalizedDefaults[path[0]]) {
          normalizedDefaults[path[0]] = {};
        }
        normalizedDefaults[path[0]][path[1]] = value;
      });
    } else {
      Object.assign(normalizedDefaults, defaults);
    }

    const obj = Object.entries(data).reduce((acc: Record<string, any>, [key, value]) => {
      acc[key] = normalizedDefaults[key] || value;
      return acc;
    }, {});

    function checkValue(valueToCheck: any): boolean {
      if (options.invalidValues!.some((key) => Array.isArray(key)) && Array.isArray(valueToCheck)) {
        return !!valueToCheck.length;
      }
      return !options.invalidValues!.includes(valueToCheck);
    }

    Object.entries(obj).forEach(([key, value]) => {
      const valueIsValid = options.invalidValues!.length ? checkValue(value) : value;

      if (!options.validate) {
        this[key] = value;
      }
      if (options.validate && valueIsValid) {
        this[key] = value;
      }
    });
  }

  exclude(keys: (keyof T)[]): FormDataProxy<T> {
    const data = Object.entries(this).reduce((acc: Record<string, any>, [key, value]) => {
      if (!keys.includes(key as keyof T)) {
        acc[key] = value;
      }
      return acc;
    }, {});
    return new FormDataProxy<T>(data as Partial<T>);
  }

  include(keys: (keyof T)[]): FormDataProxy<T> {
    const data = Object.entries(this).reduce((acc: Record<string, any>, [key, value]) => {
      if (keys.includes(key as keyof T)) {
        acc[key] = value;
      }
      return acc;
    }, {});
    return new FormDataProxy<T>(data as Partial<T>);
  }

  static validated<U = Record<string, any>>(
    data: Partial<U> = {},
    invalidValues: any[] = [],
  ): FormDataProxy<U> {
    return new FormDataProxy<U>(data, {}, { validate: true, invalidValues });
  }

  get length(): number {
    return Object.keys(this).length;
  }

  clear(): void {
    this.forEach((key: string) => {
      delete this[key];
    });
  }

  toFormData(): FormData {
    const formData = new FormData();

    const coerceDefault = (v: any): any => {
      if (typeof v === 'boolean') return v ? 1 : 0;
      return v;
    };

    Object.entries(this).forEach(([key, value]) => {
      if (value instanceof File) {
        formData.append(key, value, value.name);
      } else if (value instanceof Array) {
        if (value.length === 1 && value[0] instanceof File) {
          formData.append(key, value[0], value[0].name);
        } else {
          value.forEach((val) => {
            formData.append(`${key}[]`, val);
          });
        }
      } else {
        formData.append(key, coerceDefault(value));
      }
    });
    return formData;
  }

  send(url: string, options: AxiosRequestConfig & { contentType?: string } = {}): Promise<any> {
    const METHOD = options.method?.toLowerCase() || 'post';
    const CONTENT_TYPE = options.contentType || 'formData';

    function axiosCall(method: string, data: any) {
      switch (method) {
        case 'get':
          return axios.get(url, data);
        case 'post':
          return axios.post(url, data);
        case 'put':
          return axios.put(url, data);
        case 'patch':
          return axios.patch(url, data);
        case 'delete':
          return axios.delete(url, data);
        default:
          return axios.post(url, data);
      }
    }

    if (CONTENT_TYPE === 'formData') {
      return axiosCall(METHOD, this.toFormData());
    }

    return axiosCall(METHOD, this);
  }
}

export {
  FormDataProxy,
  FormErrors,
};
